import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import type { AppAutocompleteItem } from '@a7y/types';
import { searchAppsIndex, isAlgoliaConfigured } from './lib/algolia';

export interface AppSearchAutocompleteProps {
  /** Called when the user picks a result; the picker clears and closes immediately. */
  onSelect: (item: AppAutocompleteItem) => void;
  /**
   * Called to perform the live (non-Algolia) autocomplete fan-out. Used to
   * backfill when Algolia returns sparse results, and as the sole data source
   * when Algolia is unconfigured.
   */
  searchLive: (term: string, signal: AbortSignal) => Promise<AppAutocompleteItem[]>;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Cap visible results. Default 10. */
  maxResults?: number;
  /** Below this Algolia hit count, also fetch live and merge. Default 3. */
  backfillThreshold?: number;
  className?: string;
  inputClassName?: string;
  /** Tailwind max-height class for the scrollable list. Default fits ~3.5 rows. */
  listMaxHeightClassName?: string;
  /** Prefix for data-testid attributes (input, results, items). */
  testIdPrefix?: string;
  /**
   * Optional render override for an individual result row's content (icon +
   * name + developer + store badge). The default renders a plain `<img>` icon,
   * truncated name/developer, and a small store pill. The second argument is
   * the full list being shown, so a row can be rendered relative to the others
   * (A7Y-5620: /start labels rows that share a name within one store).
   */
  renderRow?: (item: AppAutocompleteItem, shown: readonly AppAutocompleteItem[]) => ReactNode;
  /** Restrict all visible results to one store. */
  storeFilter?: AppAutocompleteItem['store'];
  /**
   * When unscoped, sparse-looking cross-platform searches should ask the live
   * endpoint for the missing store even if Algolia already returned many hits.
   */
  preferStoreDiversity?: boolean;
  /**
   * Algolia "Served by" footer logo URLs (light + dark theme). Required by
   * Algolia brand guidelines when displaying their search results. Pass URLs
   * resolvable from the consuming app's public dir (admin's Vite base is
   * `/admin`, webapp's base is `/`). Omit to hide the footer.
   */
  algoliaLogoLightSrc?: string;
  algoliaLogoDarkSrc?: string;
  /**
   * Replaces the default "No matches." text shown when a search returns
   * zero results. Useful for surfacing a fallback affordance (e.g. "try the
   * URL paste tab") from the consuming wizard.
   */
  emptyContent?: ReactNode;
  /**
   * Optional content shown below sparse result sets after live backfill settles.
   * Consumers can use this to expose a deterministic fallback when autocomplete
   * only returns one side of a cross-platform app.
   */
  thinResultsContent?: ReactNode;
  /**
   * Instrumentation hook fired after each query settles (success or error).
   * Useful for harness/debug surfaces that want to display source attribution
   * and latency. Not fired for cache hits.
   */
  onQueryComplete?: (info: AppSearchQueryInfo) => void;
  /**
   * A7Y-1779 — fired whenever the actively-highlighted row settles (keyboard
   * ArrowUp/ArrowDown, mouse hover, the initial highlight when results open),
   * and with `null` when the dropdown closes or results empty. Consumers use
   * this to prefetch enrich for the row the user is likely to pick, so the
   * confirm step doesn't pay the full enrich round-trip on select. Optional
   * and side-effect-free with respect to selection — it never commits a row.
   */
  onHighlight?: (item: AppAutocompleteItem | null) => void;
  /**
   * A7Y-5396 — controlled mode. When `value` is set the input shows it and
   * every edit goes to `onValueChange`. A pick no longer clears the field and
   * a second Escape no longer empties it: the consumer decides what the field
   * shows (a picked app's name, a pasted store URL). Omit both for the
   * default uncontrolled picker, which clears itself on pick.
   */
  value?: string;
  onValueChange?: (value: string) => void;
  /**
   * When false the current term is not searched and the dropdown stays
   * closed — for a term the consumer already recognises (a store URL or app
   * ID) or a pick it already holds. Default true.
   */
  searchable?: boolean;
  /** Close the dropdown when the input loses focus. Default false. */
  closeOnBlur?: boolean;
  /**
   * Extra input attributes (id, name, aria-label, autoComplete, spellCheck).
   * The combobox ARIA, value and handlers are the component's and win.
   */
  inputProps?: Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange' | 'onKeyDown' | 'onFocus' | 'onBlur' | 'role' | 'type' | 'className'
  >;
  /** Class overrides for the dropdown parts; each omitted part keeps its Tailwind default. */
  classNames?: {
    dropdown?: string;
    list?: string;
    option?: string;
    optionActive?: string;
    spinner?: string;
    empty?: string;
  };
}

export interface AppSearchQueryInfo {
  term: string;
  source: 'algolia' | 'mixed' | 'live';
  durationMs: number;
  resultCount: number;
  error?: string;
}

type ResultSource = 'algolia' | 'mixed' | 'live';

const DEFAULT_PLACEHOLDER = 'Search for an app by name';
const DEBOUNCE_MS = 150;
const BACKFILL_DELAY_MS = 200;
const LRU_CAP = 50;

function keyFor(item: AppAutocompleteItem): string {
  return `${item.store}:${item.storeId}`;
}

class LRU {
  private map = new Map<string, AppAutocompleteItem[]>();
  constructor(private cap: number) {}
  get(k: string): AppAutocompleteItem[] | undefined {
    const v = this.map.get(k);
    if (!v) return undefined;
    this.map.delete(k);
    this.map.set(k, v);
    return v;
  }
  set(k: string, v: AppAutocompleteItem[]) {
    if (this.map.has(k)) this.map.delete(k);
    this.map.set(k, v);
    while (this.map.size > this.cap) {
      const first = this.map.keys().next().value;
      if (first === undefined) break;
      this.map.delete(first);
    }
  }
}

function defaultRenderRow(item: AppAutocompleteItem): ReactNode {
  const isIos = item.store === 'ios';
  return (
    <>
      {item.iconUrl ? (
        <img
          src={item.iconUrl}
          alt=""
          className="h-8 w-8 flex-shrink-0 rounded-lg object-cover shadow-sm"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="h-8 w-8 flex-shrink-0 rounded-lg bg-zinc-200 dark:bg-zinc-800" />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{item.name}</div>
        {item.developer && (
          <div className="truncate text-xs text-zinc-500 dark:text-zinc-400">{item.developer}</div>
        )}
      </div>
      <span
        className={
          'inline-flex flex-shrink-0 items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ' +
          (isIos
            ? 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
            : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300')
        }
      >
        {isIos ? 'iOS' : 'Android'}
      </span>
    </>
  );
}

function filterByStore(
  items: AppAutocompleteItem[],
  storeFilter?: AppAutocompleteItem['store']
): AppAutocompleteItem[] {
  if (!storeFilter) return items;
  return items.filter((item) => item.store === storeFilter);
}

function hasBothStores(items: AppAutocompleteItem[]): boolean {
  const stores = new Set(items.map((item) => item.store));
  return stores.has('ios') && stores.has('android');
}

export function AppSearchAutocomplete({
  onSelect,
  searchLive,
  placeholder = DEFAULT_PLACEHOLDER,
  autoFocus = false,
  disabled = false,
  maxResults = 10,
  backfillThreshold = 3,
  className,
  inputClassName,
  listMaxHeightClassName = 'max-h-[182px]',
  testIdPrefix = 'app-search',
  renderRow = defaultRenderRow,
  storeFilter,
  preferStoreDiversity,
  algoliaLogoLightSrc,
  algoliaLogoDarkSrc,
  emptyContent,
  thinResultsContent,
  onQueryComplete,
  onHighlight,
  value,
  onValueChange,
  searchable = true,
  closeOnBlur = false,
  inputProps,
  classNames,
}: AppSearchAutocompleteProps) {
  const isControlled = value !== undefined;
  const [uncontrolledTerm, setUncontrolledTerm] = useState('');
  const term = isControlled ? value : uncontrolledTerm;
  const onValueChangeRef = useRef(onValueChange);
  onValueChangeRef.current = onValueChange;
  const setTerm = useCallback(
    (next: string) => {
      if (!isControlled) setUncontrolledTerm(next);
      onValueChangeRef.current?.(next);
    },
    [isControlled]
  );
  const [results, setResults] = useState<AppAutocompleteItem[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState<ResultSource | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backfillRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const cacheRef = useRef<LRU>(new LRU(LRU_CAP));
  const listRef = useRef<HTMLUListElement | null>(null);
  const onQueryCompleteRef = useRef(onQueryComplete);
  onQueryCompleteRef.current = onQueryComplete;
  const onHighlightRef = useRef(onHighlight);
  onHighlightRef.current = onHighlight;
  const shouldPreferStoreDiversity = preferStoreDiversity ?? !storeFilter;

  // useId, not a random suffix: a prerendered page (A7Y-5396 `/start`)
  // hydrates with the same id the server wrote into aria-controls.
  const reactId = useId();
  const listboxId = `${testIdPrefix}-listbox-${reactId}`;

  const mergeDedupe = useCallback(
    (a: AppAutocompleteItem[], b: AppAutocompleteItem[]) => {
      const seen = new Set<string>();
      const out: AppAutocompleteItem[] = [];
      let missingStoreCandidate: AppAutocompleteItem | undefined;
      if (shouldPreferStoreDiversity && !storeFilter && a.length > 0 && !hasBothStores(a)) {
        const existingStore = a[0]?.store;
        missingStoreCandidate = b.find(
          (item) => item.store !== existingStore && !a.some((hit) => keyFor(hit) === keyFor(item))
        );
      }
      outer: for (const list of [a, b]) {
        for (const item of list) {
          const k = keyFor(item);
          if (seen.has(k)) continue;
          seen.add(k);
          out.push(item);
          if (out.length >= maxResults) {
            if (!missingStoreCandidate) return out;
            break outer;
          }
        }
      }
      if (
        missingStoreCandidate &&
        !out.some((item) => item.store === missingStoreCandidate.store)
      ) {
        if (out.length >= maxResults) {
          out[out.length - 1] = missingStoreCandidate;
        } else {
          out.push(missingStoreCandidate);
        }
      }
      return out;
    },
    [maxResults, shouldPreferStoreDiversity, storeFilter]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (backfillRef.current) clearTimeout(backfillRef.current);
    abortRef.current?.abort();

    const trimmed = term.trim();
    if (!trimmed || !searchable) {
      setResults([]);
      setSource(null);
      setLoading(false);
      setOpen(false);
      return;
    }

    const cacheKey = `${storeFilter ?? 'all'}:${trimmed.toLowerCase()}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setResults(cached);
      setSource(cached.length > 0 ? 'mixed' : null);
      setLoading(false);
      setOpen(true);
      setHighlight(0);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    debounceRef.current = setTimeout(() => {
      void (async () => {
        setLoading(true);
        setOpen(true);
        const startedAt = performance.now();
        const algoliaAvailable = isAlgoliaConfigured();
        let algoliaHits: AppAutocompleteItem[] = [];

        if (algoliaAvailable) {
          algoliaHits = filterByStore(
            await searchAppsIndex(trimmed, controller.signal, maxResults),
            storeFilter
          );
          if (controller.signal.aborted) return;
          if (algoliaHits.length > 0) {
            setResults(algoliaHits.slice(0, maxResults));
            setSource('algolia');
            setHighlight(0);
          }
        }

        const needsStoreDiversityBackfill =
          shouldPreferStoreDiversity &&
          !storeFilter &&
          algoliaHits.length > 0 &&
          !hasBothStores(algoliaHits);
        const needsBackfill = algoliaHits.length < backfillThreshold || needsStoreDiversityBackfill;
        if (!needsBackfill) {
          cacheRef.current.set(cacheKey, algoliaHits);
          setLoading(false);
          onQueryCompleteRef.current?.({
            term: trimmed,
            source: 'algolia',
            durationMs: performance.now() - startedAt,
            resultCount: algoliaHits.length,
          });
          return;
        }

        const runBackfill = async () => {
          try {
            const live = filterByStore(await searchLive(trimmed, controller.signal), storeFilter);
            if (controller.signal.aborted) return;
            const merged = mergeDedupe(algoliaHits, live);
            const mergedSource: ResultSource = algoliaHits.length > 0 ? 'mixed' : 'live';
            cacheRef.current.set(cacheKey, merged);
            setResults(merged);
            setSource(mergedSource);
            setHighlight(0);
            onQueryCompleteRef.current?.({
              term: trimmed,
              source: mergedSource,
              durationMs: performance.now() - startedAt,
              resultCount: merged.length,
            });
          } catch (err) {
            if (controller.signal.aborted) return;
            console.warn('[app-search] live backfill error', err);
            cacheRef.current.set(cacheKey, algoliaHits);
            onQueryCompleteRef.current?.({
              term: trimmed,
              source: algoliaHits.length > 0 ? 'algolia' : 'live',
              durationMs: performance.now() - startedAt,
              resultCount: algoliaHits.length,
              error: err instanceof Error ? err.message : String(err),
            });
          } finally {
            if (!controller.signal.aborted) setLoading(false);
          }
        };

        if (algoliaHits.length > 0) {
          backfillRef.current = setTimeout(runBackfill, BACKFILL_DELAY_MS);
        } else {
          await runBackfill();
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (backfillRef.current) clearTimeout(backfillRef.current);
      controller.abort();
    };
  }, [
    term,
    searchable,
    maxResults,
    backfillThreshold,
    searchLive,
    mergeDedupe,
    storeFilter,
    shouldPreferStoreDiversity,
  ]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (backfillRef.current) clearTimeout(backfillRef.current);
    },
    []
  );

  useEffect(() => {
    if (!open || results.length === 0) return;
    const list = listRef.current;
    if (!list) return;
    const node = list.children[highlight] as HTMLElement | undefined;
    if (typeof node?.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [highlight, open, results.length]);

  // A7Y-1779 — surface the settled highlight to consumers so they can prefetch
  // enrich for the row the user is likely to pick. Mirrors the
  // `onQueryCompleteRef` pattern above (ref so the effect deps stay stable).
  // Fires `null` when the dropdown is closed or there are no results.
  useEffect(() => {
    onHighlightRef.current?.(
      open && results.length ? results[Math.min(highlight, results.length - 1)] : null
    );
  }, [highlight, open, results]);

  const selectItem = (item: AppAutocompleteItem) => {
    // End this search before handing the selection to a consumer that may
    // keep the picker mounted (for example, while adding several competitors).
    abortRef.current?.abort();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (backfillRef.current) clearTimeout(backfillRef.current);
    if (!isControlled) setUncontrolledTerm('');
    setResults([]);
    setOpen(false);
    setLoading(false);
    setSource(null);
    setHighlight(0);
    onSelect(item);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      if (results.length === 0) return;
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h + 1 >= results.length ? 0 : h + 1));
    } else if (e.key === 'ArrowUp') {
      if (results.length === 0) return;
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h <= 0 ? results.length - 1 : h - 1));
    } else if (e.key === 'Enter') {
      if (open && results.length > 0) {
        e.preventDefault();
        const item = results[Math.min(highlight, results.length - 1)];
        if (item) selectItem(item);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (open) setOpen(false);
      else if (!isControlled) {
        setTerm('');
        setResults([]);
      }
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const showDropdown = open && searchable && term.trim().length > 0;
  const showEmpty = showDropdown && !loading && results.length === 0;
  const showThinResults =
    showDropdown &&
    !loading &&
    results.length > 0 &&
    results.length < backfillThreshold &&
    Boolean(thinResultsContent);
  const showAlgoliaFooter =
    results.length > 0 &&
    (source === 'algolia' || source === 'mixed') &&
    Boolean(algoliaLogoLightSrc || algoliaLogoDarkSrc);
  const activeDescendantId =
    open && results.length > 0
      ? `${listboxId}-opt-${Math.min(highlight, results.length - 1)}`
      : undefined;

  return (
    <div className={`relative ${className ?? ''}`}>
      <div className="relative w-full">
        <input
          {...inputProps}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={activeDescendantId}
          autoFocus={autoFocus}
          disabled={disabled}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onFocus={() => {
            if (searchable && term.trim() && results.length > 0) setOpen(true);
          }}
          onBlur={closeOnBlur ? () => setOpen(false) : undefined}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className={
            inputClassName ??
            'w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-900'
          }
          data-testid={`${testIdPrefix}-input`}
        />
        {loading && (
          <div
            className={classNames?.spinner ?? 'pointer-events-none absolute right-3 top-1/2 -translate-y-1/2'}
            data-testid={`${testIdPrefix}-spinner`}
          >
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-zinc-400 border-t-transparent" />
          </div>
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div
          className={
            classNames?.dropdown ??
            'absolute left-0 right-0 top-full z-50 mt-2 w-full rounded-xl border border-zinc-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900'
          }
        >
          <ul
            ref={listRef}
            role="listbox"
            id={listboxId}
            className={
              classNames?.list ??
              `w-full ${listMaxHeightClassName} overflow-y-auto divide-y divide-zinc-100 dark:divide-zinc-800`
            }
            data-testid={`${testIdPrefix}-results`}
          >
            {results.map((item, idx) => {
              const selected = idx === highlight;
              const optId = `${listboxId}-opt-${idx}`;
              return (
                <li
                  key={keyFor(item)}
                  id={optId}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(e) => {
                    // A7Y-1412 — commit selection on mousedown rather than
                    // click. Two reasons: (1) preventDefault keeps focus on
                    // the input so blur/close doesn't swallow the click;
                    // (2) mousedown fires against the row that was under the
                    // cursor when the press began, so a late reorder between
                    // mousedown and mouseup can't cause the user to commit
                    // a different row than the one they pressed.
                    e.preventDefault();
                    selectItem(item);
                  }}
                  className={
                    classNames?.option !== undefined
                      ? `${classNames.option}${selected && classNames.optionActive ? ` ${classNames.optionActive}` : ''}`
                      : 'flex cursor-pointer items-center gap-3 px-3 py-1.5 ' +
                        (selected
                          ? 'bg-zinc-100 dark:bg-zinc-800/60'
                          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/40')
                  }
                  data-testid={`${testIdPrefix}-result-${idx}`}
                >
                  {renderRow(item, results)}
                </li>
              );
            })}
          </ul>
          {showAlgoliaFooter && (
            <div className="flex items-center justify-end gap-1.5 border-t border-zinc-100 px-3 py-1 text-[10px] text-zinc-400 dark:border-zinc-800">
              <span>Served by:</span>
              {algoliaLogoLightSrc && (
                <img
                  src={algoliaLogoLightSrc}
                  alt="Algolia"
                  className={algoliaLogoDarkSrc ? 'h-3 dark:hidden' : 'h-3'}
                />
              )}
              {algoliaLogoDarkSrc && (
                <img
                  src={algoliaLogoDarkSrc}
                  alt="Algolia"
                  className={algoliaLogoLightSrc ? 'hidden h-3 dark:inline' : 'h-3'}
                />
              )}
            </div>
          )}
          {showThinResults && (
            <div
              className="border-t border-zinc-100 px-3 py-2 text-center text-xs text-zinc-500 dark:border-zinc-800"
              data-testid={`${testIdPrefix}-thin-results`}
            >
              {thinResultsContent}
            </div>
          )}
        </div>
      )}

      {showEmpty && (
        <div
          className={classNames?.empty ?? 'mt-3 text-center text-sm text-zinc-500'}
          data-testid={`${testIdPrefix}-empty`}
        >
          {emptyContent ?? 'No matches.'}
        </div>
      )}
    </div>
  );
}
